import { Button } from "@mui/material";
import { IntermediatePlate } from "features/WorklistTools/shared/components/IntermediatePlate";
import { NumberField } from "features/WorklistTools/shared/components/NumberField";
import { PlateBarcodeField } from "features/WorklistTools/shared/components/PlateBarcodeField";
import { PlateType } from "features/WorklistTools/shared/components/PlateType";
import {
  PlatePosition,
  WellSelection,
  WorklistTools,
} from "features/WorklistTools/shared/interfaces";
import { arrangeColWise } from "features/WorklistTools/shared/WorklistHelpers";
import { useAppReduxDispatch, useAppReduxSelector } from "hooks/reduxHooks";
import styled from "styled-components";
import {
  ExternalThawToolActions,
  IntermediatePlateInfoState,
  SourcePlateInfoState,
} from "../state";
import { ExternalThawInternalAction } from "../state/actions";
import { useExternalThawStateProvider } from "../state/StateProvider";
import { IntWellVol } from "./components/IntWellVol";
import { srcPlateToWellSelections } from "./handlers/SrcPlateToWellSelections";

const StyledContainerDiv = styled.div`
  height: calc(100vh - 300px);
  width: 600px;
  overflow: auto;
`;

const StyledIntContainerDiv = styled.div`
  height: 320px;
  width: 100%;
`;

const StyledNumFieldDiv = styled.div`
  display: flex;
  width: 100%;
`;

const StyledButtonDiv = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
`;

const StyledPlateSettingsContainer = styled.div`
  margin-top: 10px;
  width: 100%;
`;

export const IntermediatePlatesContainer = (intPlateTypeInfo: any) => {
  const { externalThawInternalState, externalThawDispatch } =
    useExternalThawStateProvider();

  const dispatch = useAppReduxDispatch();
  const sourcePlateInfo: SourcePlateInfoState[] = useAppReduxSelector(
    (state) => state.WorklistTools.ExternalThawTool.present.sourcePlateInfo
  );
  const worklistValues = useAppReduxSelector(
    (state) => state.WorklistTools.ExternalThawTool.present.worklistValues
  );
  const intPlateInfo = useAppReduxSelector(
    (state) => state.WorklistTools.ExternalThawTool.present.intPlateInfo
  );

  const handlePlateTypeChanged = (index: number, plateTypeInfo: any) => {
    dispatch(
      ExternalThawToolActions.SET_INTERMEDIATE_PLATE({
        index,
        rows: plateTypeInfo.plateRows,
        cols: plateTypeInfo.plateCols,
        operatingVol: plateTypeInfo.defaultLabwareVolume,
        labwareTypeCode: plateTypeInfo.labwareTypeCode,
      })
    );
  };

  const handleIntPlateInfoChanged = (
    index: number,
    key: string,
    value: any
  ) => {
    dispatch(
      ExternalThawToolActions.UPDATE_INT_PLATE_INFO({
        index,
        key,
        value,
      })
    );
  };

  const handleWellHover = (wellId: string) => {
    externalThawDispatch({
      type: ExternalThawInternalAction.SET_HOVER_WELL_ID,
      payload: {
        hoverWellId: wellId,
      },
    });
  };

  const nextOpenIntWell = (): WellSelection => {
    //TODO?: Refactor to make it dynamic (if needed)
    //if harvestWells.length === 0 then A1 otherwise find next open well

    return {
      plateIndex: 0,
      plateWellId: "A1",
      isSelectable: true,
    };
  };

  const handleAddToIntPlate = async () => {
    const wellSelections = srcPlateToWellSelections(sourcePlateInfo);

    const harvestWells = await arrangeColWise(
      worklistValues.harvestWells,
      wellSelections,
      nextOpenIntWell(),
      intPlateInfo[0].rows,
      intPlateInfo[0].cols,
      PlatePosition.Intermediate,
      false
    );

    if (harvestWells.length > 0) {
      dispatch(
        ExternalThawToolActions.SRC_TO_INT_MANUALLY_ADDED({
          selections: harvestWells,
        })
      );
    }
  };

  const intOrSrcNotSelected = () => {
    return (
      sourcePlateInfo[0].plateBarcode.length === 0 ||
      intPlateInfo[0].labwareTypeCode.length === 0
    );
  };

  return (
    <StyledContainerDiv>
      {intPlateInfo.map((item: IntermediatePlateInfoState, index: number) => (
        <div style={{ padding: "20px" }}>
          <PlateBarcodeField
            plateRole={PlatePosition.Intermediate}
            index={index}
            label={`Intermediate Plate Barcode ${index + 1}`}
            plateBarcode={item.plateBarcode}
            handlePlateBarcodeChange={(index, field, value) =>
              handleIntPlateInfoChanged(index!, field, value)
            }
            disabled={item.plateBarcode === ""}
          />
          <StyledIntContainerDiv>
            <IntermediatePlate
              worklistTool={WorklistTools.ExternalThaw}
              index={index}
              plateRows={item.rows}
              plateCols={item.cols}
              wellMapping={worklistValues.harvestWells}
              sourcePlateInfo={sourcePlateInfo}
              sourcePlatePosition={
                index === 0 ? PlatePosition.Source : PlatePosition.Intermediate
              }
              handleWellHover={handleWellHover}
            />
          </StyledIntContainerDiv>
          <StyledPlateSettingsContainer>
            <PlateType
              platePosition={PlatePosition.Intermediate}
              isDisabled={
                externalThawInternalState.methodSettings!.selectedSystem === 0
              }
              acceptedPlateTypes={intPlateTypeInfo.intPlateInfo}
              plateType={item.labwareTypeCode}
              setPlateType={(plateTypeInfo) =>
                handlePlateTypeChanged(index, plateTypeInfo)
              }
            />
            <StyledNumFieldDiv>
              <NumberField
                style={{
                  width: "50%",
                  marginTop: "10px",
                  marginRight: "5px",
                }}
                id="operating-vol"
                label="Operating Vol (µL)"
                value={item.operatingVol}
                onChange={(value) =>
                  handleIntPlateInfoChanged(
                    index,
                    "operatingVol",
                    parseInt(value)
                  )
                }
                disabled={true}
              />
              <IntWellVol
                intWellVol={item.intWellVol}
                operatingVol={item.operatingVol}
              />
            </StyledNumFieldDiv>
            <StyledButtonDiv>
              <Button
                variant="contained"
                onClick={handleAddToIntPlate}
                disabled={intOrSrcNotSelected()}
                sx={{ marginTop: "10px", width: "50%" }}
              >
                Add Wells to Int Plate
              </Button>
            </StyledButtonDiv>
          </StyledPlateSettingsContainer>
        </div>
      ))}
    </StyledContainerDiv>
  );
};
